import { defineStore } from 'pinia'
import { ref } from 'vue'
import api from '../api'
import { useUserStore } from './userStore'
import type { ChatMessageDto } from '../dtos'
import { useChatNotificationStore } from './chatNotificationStore'

export const useChatStore = defineStore('chat', () => {
    const userStore = useUserStore()
    const messages = ref<ChatMessageDto[]>([])
    const loading = ref(false)
    const uploading = ref(false)
    const hasMore = ref(true)
    const activeBandId = ref<number | null>(null)
    const activeSongId = ref<number | null>(null)
    const newMessageFlag = ref(0)

    let pollTimer: number | null = null

    async function fetchMessages(bandId: number, songId?: number) {
        if (!userStore.user) return
        loading.value = true
        activeBandId.value = bandId
        activeSongId.value = songId ?? null

        try {
            const token = await userStore.user.getIdToken()
            const url = songId ? `/chat/song/${songId}` : `/chat/band/${bandId}`
            const response = await api.get(url, {
                headers: { Authorization: `Bearer ${token}` }
            })
            messages.value = response.data
            hasMore.value = response.data.length >= 50
        } catch (error) {
            console.error('Failed to fetch messages:', error)
        } finally {
            loading.value = false
        }
    }

    async function loadOlderMessages() {
        if (!userStore.user || !activeBandId.value || !hasMore.value || messages.value.length === 0) return

        const oldestId = messages.value[0]?.id || 0
        try {
            const token = await userStore.user.getIdToken()
            const url = activeSongId.value ? `/chat/song/${activeSongId.value}` : `/chat/band/${activeBandId.value}`
            const response = await api.get(url, {
                params: { beforeId: oldestId, count: 50 },
                headers: { Authorization: `Bearer ${token}` }
            })
            const older: ChatMessageDto[] = response.data
            hasMore.value = older.length >= 50
            messages.value = [...older, ...messages.value]
        } catch (error) {
            console.error('Failed to load older messages:', error)
        }
    }

    async function sendMessage(content: string) {
        if (!userStore.user || !activeBandId.value) return

        try {
            const token = await userStore.user.getIdToken()
            const body: Record<string, unknown> = {
                content,
                bandId: activeBandId.value
            }
            if (activeSongId.value) {
                body.songId = activeSongId.value
            }
            const response = await api.post('/chat', body, {
                headers: { Authorization: `Bearer ${token}` }
            })
            messages.value.push(response.data)
            return response.data
        } catch (error) {
            console.error('Failed to send message:', error)
            throw error
        }
    }

    async function sendMessageWithAttachment(content: string, file: File) {
        if (!userStore.user || !activeBandId.value) return

        uploading.value = true
        try {
            const token = await userStore.user.getIdToken()
            const formData = new FormData()
            formData.append('content', content)
            formData.append('bandId', activeBandId.value.toString())
            if (activeSongId.value) {
                formData.append('songId', activeSongId.value.toString())
            }
            formData.append('file', file)

            const response = await api.post('/chat/with-attachment', formData, {
                headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
            })
            messages.value.push(response.data)
            return response.data
        } catch (error) {
            console.error('Failed to send message with attachment:', error)
            throw error
        } finally {
            uploading.value = false
        }
    }

    async function pollNewMessages() {
        if (!userStore.user || !activeBandId.value) return

        const lastId = messages.value.length > 0 ? messages.value[messages.value.length - 1]?.id || 0 : 0
        try {
            const token = await userStore.user.getIdToken()
            const params: Record<string, unknown> = { afterId: lastId }
            if (activeSongId.value) {
                params.songId = activeSongId.value
            }
            const response = await api.get(`/chat/band/${activeBandId.value}/new`, {
                params,
                headers: { Authorization: `Bearer ${token}` }
            })
            const newMessages: ChatMessageDto[] = response.data
            if (newMessages.length > 0) {
                const existingIds = new Set(messages.value.map(m => m.id))
                const trulyNew = newMessages.filter(m => !existingIds.has(m.id))
                if (trulyNew.length > 0) {
                    messages.value.push(...trulyNew)
                    newMessageFlag.value++
                    // Mark as read so notifications don't show for messages we're already seeing
                    const notifStore = useChatNotificationStore()
                    if (activeSongId.value) {
                        notifStore.markSongChatAsRead(activeBandId.value!, activeSongId.value)
                    } else {
                        notifStore.markBandChatAsRead(activeBandId.value!)
                    }
                }
            }
        } catch {
            // Silent fail on poll
        }
    }

    async function editMessage(messageId: number, content: string) {
        if (!userStore.user) return
        try {
            const token = await userStore.user.getIdToken()
            const response = await api.put(`/chat/${messageId}`, { content }, {
                headers: { Authorization: `Bearer ${token}` }
            })
            const idx = messages.value.findIndex(m => m.id === messageId)
            if (idx !== -1) {
                messages.value[idx] = response.data
            }
        } catch (error) {
            console.error('Failed to edit message:', error)
            throw error
        }
    }

    async function deleteMessage(messageId: number) {
        if (!userStore.user) return
        try {
            const token = await userStore.user.getIdToken()
            await api.delete(`/chat/${messageId}`, {
                headers: { Authorization: `Bearer ${token}` }
            })
            messages.value = messages.value.filter(m => m.id !== messageId)
        } catch (error) {
            console.error('Failed to delete message:', error)
            throw error
        }
    }

    function startPolling(bandId: number, songId?: number) {
        stopPolling()
        activeBandId.value = bandId
        activeSongId.value = songId ?? null
        pollTimer = window.setInterval(() => pollNewMessages(), 5000)
    }

    function stopPolling() {
        if (pollTimer) {
            clearInterval(pollTimer)
            pollTimer = null
        }
    }

    function clear() {
        stopPolling()
        messages.value = []
        loading.value = false
        hasMore.value = true
        activeBandId.value = null
        activeSongId.value = null
    }

    return {
        messages, loading, uploading, hasMore, activeBandId, activeSongId, newMessageFlag,
        fetchMessages, loadOlderMessages, sendMessage, sendMessageWithAttachment, editMessage, deleteMessage,
        startPolling, stopPolling, clear
    }
})
